<template>
  <div class="announcements-management">
    <div class="page-header">
      <div class="page-breadcrumb">
        <span>公告管理</span>
        <span v-if="viewMode === 'form'"><i class="fas fa-chevron-right separator"></i> {{ isEditMode ? '編輯公告' : '新增公告' }}</span>
        <span v-else><i class="fas fa-chevron-right separator"></i> 公告列表</span>
      </div>
    </div>

    <div v-if="viewMode === 'list'">
      <div class="filter-bar">
        <input type="text" v-model="filters.title" placeholder="依標題搜尋..." class="form-input search-input" @keyup.enter="applyFiltersAndResetPage"/>
        <select v-model="filters.enabled" class="form-input search-select" @change="applyFiltersAndResetPage">
          <option :value="null">啟用狀態 (全部)</option>
          <option :value="true">已啟用</option>
          <option :value="false">已停用</option>
        </select>
        <button class="filter-btn" @click="applyFiltersAndResetPage">
          <i class="fas fa-search"></i> 搜尋
        </button>
        <button class="clear-filter-btn" @click="clearFiltersAndFetch">
          <i class="fas fa-times"></i> 清除
        </button>
      </div>

      <div class="main-actions">
        <button class="action-btn-primary" @click="showFormView()">
          <i class="fas fa-plus"></i> 新增公告
        </button>
      </div>

      <div v-if="isLoading" class="loading-spinner">
        <p><i class="fas fa-spinner fa-spin"></i> 載入公告中...</p>
      </div>

      <div v-if="!isLoading && announcements.length > 0" class="data-table-container">
        <table class="data-table">
          <thead>
          <tr>
            <th>封面圖</th>
            <th>標題</th>
            <th>類型</th>
            <th>狀態</th>
            <th>排序</th>
            <th>最後更新時間</th>
            <th>操作</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in announcements" :key="item.id">
            <td data-label="封面圖" class="cover-image-cell">
              <img v-if="item.coverImageUrl" :src="item.coverImageUrl" alt="Cover" class="table-cover-image">
              <span v-else class="no-image-placeholder">-</span>
            </td>
            <td data-label="標題">{{ item.title }}</td>
            <td data-label="類型">{{ item.type }}</td>
            <td data-label="狀態">
            <span :class="['status-badge', item.enabled ? 'enabled' : 'disabled']">
              {{ item.enabled ? '已啟用' : '已停用' }}
            </span>
            </td>
            <td data-label="排序">{{ item.sort }}</td>
            <td data-label="最後更新">{{ formatDateTime(item.updatedAt) }}</td>
            <td class="actions-cell">
              <button class="action-btn edit-btn" @click="showFormView(item)">
                <i class="fas fa-edit"></i> 編輯
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(item)">
                <i class="fas fa-trash-alt"></i> 刪除
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading && announcements.length === 0" class="no-data-message">
        <i class="fas fa-dungeon"></i> 目前沒有任何公告。
      </div>

      <div class="pagination-controls" v-if="!isLoading && totalItems > 0">
        <div class="items-per-page-selector">
          <label for="itemsPerPageSelect">每頁顯示：</label>
          <select id="itemsPerPageSelect" v-model.number="itemsPerPage" @change="handleItemsPerPageChange" class="select-dropdown">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span class="total-items-info"> (共 {{ totalItems }} 項)</span>
        </div>
        <div class="page-navigation">
          <button @click="goToPage(1)" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button @click="prevPage" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-angle-left"></i>
          </button>
          <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 頁</span>
          <button @click="nextPage" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
            <i class="fas fa-angle-right"></i>
          </button>
          <button @click="goToPage(totalPages)" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
            <i class="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-else>
      <AnnouncementEdit
          v-if="viewMode === 'form'"
          :announcementId="currentAnnouncementId"
          @close="showListView"
          @saved="handleSaveSuccess"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { getAnnouncementPage, deleteAnnouncement } from '@/api/announcement.api.js';
import Swal from '@/utils/chineseSwal';
import AnnouncementEdit from '@/components/AnnouncementEdit.vue';

// --- 視圖控制 ---
const viewMode = ref('list'); // 'list' 或 'form'
const isEditMode = ref(false);
const currentAnnouncementId = ref(null);

const showFormView = (announcement = null) => {
  if (announcement) {
    isEditMode.value = true;
    currentAnnouncementId.value = announcement.id;
  } else {
    isEditMode.value = false;
    currentAnnouncementId.value = null;
  }
  viewMode.value = 'form';
};
const showListView = () => {
  viewMode.value = 'list';
  currentAnnouncementId.value = null;
};
const handleSaveSuccess = () => {
  fetchData();
  showListView();
};

// --- 列表頁原有邏輯 ---
const toast = useToast();
const announcements = ref([]);
const isLoading = ref(true);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(0);
const totalItems = ref(0);
const filters = reactive({ title: '', enabled: null });

const fetchData = async () => {
  isLoading.value = true;
  const queryRequest = { title: filters.title.trim() || undefined, enabled: filters.enabled === null ? undefined : filters.enabled };
  Object.keys(queryRequest).forEach(key => queryRequest[key] === undefined && delete queryRequest[key]);
  const pageable = { page: currentPage.value - 1, size: itemsPerPage.value, sort: 'sort,asc' };
  try {
    const response = await getAnnouncementPage({ ...queryRequest, ...pageable });
    announcements.value = response.content;
    totalItems.value = response.totalElements;
    totalPages.value = response.totalPages;
    if (announcements.value.length === 0 && totalItems.value > 0 && currentPage.value > 1) {
      currentPage.value = totalPages.value > 0 ? totalPages.value : 1;
    }
  } catch (error) { toast.error('獲取公告失敗，請稍後再試。');
  } finally { isLoading.value = false; }
};

onMounted(fetchData);
watch([currentPage, itemsPerPage], fetchData);

const applyFiltersAndResetPage = () => {
  currentPage.value = 1;
  fetchData();
};
const clearFiltersAndFetch = () => {
  filters.title = '';
  filters.enabled = null;
  applyFiltersAndResetPage();
};

const handleDelete = (announcement) => {
  Swal.fire({
    title: '確定要刪除嗎？',
    html: `您即將刪除公告：<br><b>「${announcement.title}」</b><br>此操作無法復原！`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: '確定刪除',
    cancelButtonText: '取消',
    customClass: { /* ... Swal 樣式 ... */ },
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await deleteAnnouncement(announcement.id);
        toast.success(`公告 "${announcement.title}" 已成功刪除！`);
        await fetchData();
      } catch (error) {
        toast.error(`刪除公告失敗：${error.response?.data?.message || '操作失敗'}`);
      }
    }
  });
};

const goToPage = (page) => { if (page >= 1 && page <= totalPages.value) currentPage.value = page; };
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++; };
const prevPage = () => { if (currentPage.value > 1) currentPage.value--; };
const handleItemsPerPageChange = () => { applyFiltersAndResetPage(); };
const formatDateTime = (dateTime) => { return dateTime ? new Date(dateTime).toLocaleString('zh-TW') : '-'; };
</script>

<style scoped>
/* 這裡只保留列表頁需要的樣式 */
.announcements-management { padding: 20px; }
.page-breadcrumb { font-size: 0.95em; color: #a0a0b0; margin-bottom: 25px; display: flex; align-items: center; }
.page-breadcrumb .separator { margin: 0 8px; font-size: 0.7em; }
.filter-bar { display: flex; flex-wrap: wrap; gap: 15px; margin-bottom: 15px; align-items: center; background-color: rgba(30, 30, 40, 0.7); padding: 15px; border-radius: 8px; border: 1px solid rgba(160, 100, 220, 0.3); }
.main-actions { margin-bottom: 25px; }
.form-input { padding: 10px 12px; background-color: rgba(20, 20, 30, 0.8); border: 1px solid rgba(160, 100, 220, 0.4); border-radius: 5px; color: #e0e0e0; font-size: 1em; box-sizing: border-box; height: 38px; flex-grow: 1; }
.form-input:focus { outline: none; border-color: #ff8c69; box-shadow: 0 0 8px rgba(255, 140, 105, 0.3); }
.form-input.search-input { flex: 2 1 200px; }
.form-input.search-select { flex: 1 1 150px; padding: 0 12px;}
.filter-btn, .clear-filter-btn { padding: 0 15px; height: 38px; border-radius: 5px; cursor: pointer; font-weight: 500; font-size: 0.9em; transition: all 0.2s ease; border: 1px solid transparent; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; }
.filter-btn i, .clear-filter-btn i { margin-right: 6px; }
.filter-btn { background: linear-gradient(145deg, #8be9fd, #50c8ea); color: #1a1a1a; border-color: #50c8ea; }
.clear-filter-btn { background-color: rgba(80, 80, 90, 0.7); color: #c0c0c0; border-color: rgba(100,100,110,0.8); }
.action-btn-primary { padding: 10px 20px; border-radius: 5px; cursor: pointer; font-weight: 500; font-size: 1em; transition: all 0.2s ease; background: linear-gradient(145deg, #ff8c69, #e06c55); color: #ffffff; border: none; }
.action-btn-primary i { margin-right: 8px; }
.loading-spinner, .no-data-message { text-align: center; padding: 40px; font-size: 1.1em; color: #888e95; background-color: rgba(30, 30, 40, 0.7); border: 1px solid rgba(160, 100, 220, 0.3); border-radius: 8px; }
.no-data-message i { margin-right: 10px; }
.data-table-container { background-color: rgba(30, 30, 40, 0.7); border: 1px solid rgba(160, 100, 220, 0.3); border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.3); overflow-x: auto; margin-bottom: 30px; }
.data-table { width: 100%; border-collapse: collapse; color: #c0c0c0; }
.data-table th, .data-table td { padding: 12px 15px; text-align: left; border-bottom: 1px solid rgba(160, 100, 220, 0.15); vertical-align: middle; }
.data-table th { background-color: rgba(40, 40, 55, 0.8); font-weight: 600; color: #e0e0e0; font-size: 0.9em; text-transform: uppercase; }
.data-table tr:last-child td { border-bottom: none; }
.status-badge { padding: 4px 10px; border-radius: 12px; font-size: 0.85em; font-weight: 500; }
.status-badge.enabled { background-color: #50fa7b; color: #1a1a1a; }
.status-badge.disabled { background-color: #ff5555; color: #ffffff; }
.actions-cell { text-align: right; white-space: nowrap; }
.action-btn { background: none; border: 1px solid transparent; color: #adb5bd; padding: 6px 12px; border-radius: 4px; cursor: pointer; transition: all 0.2s ease; font-size: 0.9em; margin-left: 8px; }
.action-btn i { margin-right: 5px; }
.action-btn.edit-btn { border-color: rgba(139, 233, 253, 0.5); color: #8be9fd; }
.action-btn.delete-btn { border-color: rgba(255, 85, 85, 0.5); color: #ff5555; }
.pagination-controls { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 15px; }
.items-per-page-selector, .page-navigation { display: flex; align-items: center; gap: 8px; color: #a0a0b0; }
.select-dropdown { background-color: rgba(20, 20, 30, 0.8); border: 1px solid rgba(160, 100, 220, 0.4); color: #e0e0e0; border-radius: 4px; padding: 6px; }
.page-btn { background-color: rgba(80, 80, 90, 0.7); border: 1px solid rgba(100,100,110,0.8); color: #c0c0c0; width: 32px; height: 32px; border-radius: 4px; cursor: pointer; }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-info { font-weight: bold; color: #e0e0e0; }
.cover-image-cell {
  width: 100px; /* 給定一個固定寬度 */
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.table-cover-image {
  width: 80px;
  height: 45px; /* 維持 16:9 比例 */
  object-fit: cover;
  border-radius: 4px;
  display: block;
}
.no-image-placeholder {
  color: #6c757d;
}
/* RWD for table - 手機版樣式調整 */
@media (max-width: 768px) {
  .data-table td:before {
    color: #a0a0b0;
  }
  /* 針對圖片欄位特別調整 */
  .data-table td.cover-image-cell {
    padding-left: 50%; /* 維持對齊 */
  }
  .data-table td.cover-image-cell .table-cover-image {
    max-width: 120px; /* 手機上可以稍大一點 */
    width: auto;
    height: auto;
    float: right; /* 讓圖片靠右 */
  }
  .data-table td.cover-image-cell .no-image-placeholder {
    float: right;
  }
}
</style>